<template>
  <VueDatePicker
    v-model="model"
    :disabled="readOnly"
    :disabled-dates="disabledDates"
    :enable-time-picker="false"
    :locale="transformLocaleToDayjsFormat(locale)"
    :max-date="MILESTONES_MAX_DATE"
    :min-date="MILESTONES_MIN_DATE"
    :month-picker="monthPicker"
    :offset
    :position
    :quarter-picker="quarterPicker"
    :ui="{
      menu: 'avdp-Menu'
    }"
    :week-numbers="weekNumbers"
    :week-picker="weekPicker"
    auto-apply
    teleport
  >
    <template #calendar-header="{ day }">
      {{ day }}
    </template>
    <template #trigger>
      <div
        :class="{
          'avdp-Trigger-readonly': readOnly
        }"
        class="avdp-Trigger"
      >
        <label v-if="label" class="avdp-Trigger_Label">
          {{ label }}
        </label>

        <slot :content="triggerContent" name="button">
          <div class="avdp-Trigger_Button">
            <span class="oboard-truncated-text">
              {{ triggerContent }}
            </span>

            <AppIcon class="avdp-Trigger_Icon" height="24" icon-name="calendar-next" width="24" />
          </div>
        </slot>
      </div>
    </template>
    <template #arrow-right>
      <AppIcon height="24" icon-name="arrow-right-simple-next" width="24" />
    </template>
    <template #arrow-left>
      <AppIcon height="24" icon-name="arrow-left-simple-next" width="24" />
    </template>
  </VueDatePicker>
</template>

<script setup>
import VueDatePicker from '@vuepic/vue-datepicker'
import dayjs from 'dayjs'
import { isEmpty } from 'lodash'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import {
  createDatesRange,
  ELEMENT_PERIODS_DATE_FORMAT,
  getLabelForQuarter,
  getMonthStartDateFromObject,
  MILESTONE_DAY_FORMAT,
  MILESTONE_MONTH_FORMAT,
  MILESTONES_MAX_DATE,
  MILESTONES_MIN_DATE
} from '@/utils/date'
import { datePickerModePropValidator } from '@/utils/prop-validators'
import { transformLocaleToDayjsFormat } from '@jira/util'

import AppIcon from '@/components/ui/AppIcon/AppIcon'
import '@vuepic/vue-datepicker/dist/main.css'

defineOptions({
  name: 'AppVueDatePicker'
})

const props = defineProps({
  disabledDates: {
    type: [Array, Function],
    default: () => []
  },

  weekPicker: {
    type: Boolean,
    validator: datePickerModePropValidator
  },

  monthPicker: {
    type: Boolean,
    validator: datePickerModePropValidator
  },

  quarterPicker: {
    type: Boolean,
    validator: datePickerModePropValidator
  },

  label: {
    type: String,
    default: ''
  },

  readOnly: {
    type: Boolean
  },

  position: {
    type: String,
    default: 'left'
  },

  offset: {
    type: Number,
    default: 0
  }
})

const { locale, t } = useI18n()

const formatDate = date => dayjs(date).format(ELEMENT_PERIODS_DATE_FORMAT)

const model = defineModel({
  type: [Array, Object, Number, String],
  required: true,
  set: value => {
    if (props.readOnly) return

    if (props.monthPicker) return value

    return Array.isArray(value) ? value.map(v => formatDate(v)) : formatDate(value)
  }
})

// const weekNumber = computed(() => {
//   if (props.weekPicker && model.value && !isEmpty(model.value)) {
//     const weekNumber = dayjs(model.value[0]).format('W YYYY')
//
//     return `W${weekNumber}`
//   }
//
//   return ''
// })

const triggerContent = computed(() => {
  if (!model.value || isEmpty(model.value)) {
    const [keyword] = [
      props.weekPicker && t('units.week'),
      props.monthPicker && t('units.month'),
      props.quarterPicker && t('units.quarter'),
      t('common.date')
    ].filter(Boolean)

    return `Select ${keyword.toLowerCase()}`
  }

  if (props.weekPicker) return createDatesRange(model.value)

  if (props.monthPicker)
    return getMonthStartDateFromObject(model.value).format(MILESTONE_MONTH_FORMAT)

  if (props.quarterPicker) {
    return getLabelForQuarter(model.value)
  }

  return dayjs(model.value).format(MILESTONE_DAY_FORMAT)
})

const weekNumbers = computed(() => {
  return props.weekPicker ? 'iso' : undefined
})
</script>

<style lang="scss" scoped></style>

<style lang="scss">
.dp__arrow_top,
.dp__arrow_bottom {
  .avdp-Menu & {
    display: none;
  }
}

.avdp-Menu {
  --dp-font-family: $system-ui;
  --dp-menu-padding: 16px 10px 12px;
  --dp-border-radius: 8px;
  --dp-cell-size: 33px;
  --dp-secondary-color: var(--grey-1-next);
  --dp-primary-color: var(--primary-color-next);
  --dp-icon-color: var(--dark-1);

  .dp--year-select,
  .dp__month_year_select {
    font-weight: fw('semi-bold');
    color: var(--dark-1);
    font-size: $fs-14;
  }

  .dp__calendar_header {
    --dp-text-color: var(--grey-semi-medium);
    font-weight: fw('bold');
    font-size: $fs-12;
    align-content: center;
  }

  .dp__cell_inner {
    font-size: $fs-12;
  }

  .dp__calendar_item {
    flex-grow: 0;
  }
}

.avdp-Trigger {
  display: flex;
  gap: 6px;
  flex-direction: column;
  width: 100%;
}

.avdp-Trigger_Button {
  height: 40px;
  font-family: $system-ui;
  border: 2px solid $grey-2-next;
  border-radius: $border-radius-sm-next;
  padding: 6px 6px 6px 8px;
  font-size: $fs-14;
  font-weight: fw('semi-bold');
  line-height: 20px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;

  &:not(.avdp-Trigger-readonly &) {
    background-color: $white;
    cursor: pointer;
    color: $dark-1;
  }

  .avdp-Trigger-readonly & {
    cursor: not-allowed;
    background-color: $grey-2-next;
    color: $dark-3;
  }
}

.avdp-Trigger_Icon {
  flex-shrink: 0;

  &:not(.avdp-Trigger-readonly &) {
    color: $dark-2;
  }

  .avdp-Trigger-readonly & {
    color: inherit;
  }
}

.avdp-Trigger_Label {
  font-weight: fw('bold');
  font-size: $fs-12;
  line-height: 16px;

  &:not(.avdp-Trigger-readonly &) {
    color: $dark-3;
  }

  .avdp-Trigger-readonly & {
    color: $grey-1-next;
  }
}
</style>
